import React from 'react';
import { Link } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { SiAmericanexpress, SiMastercard, SiMercadopago, SiPix, SiVisa } from 'react-icons/si';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import emailjs from '@emailjs/browser';

const paymentMethods = [
  { label: 'Mercado Pago', icon: SiMercadopago, color: '#00B1EA' },
  { label: 'Pix', icon: SiPix, color: '#32BCAD' },
  { label: 'Visa', icon: SiVisa, color: '#1434CB' },
  { label: 'Mastercard', icon: SiMastercard, color: '#EB001B' },
  { label: 'American Express', icon: SiAmericanexpress, color: '#2E77BC' }
];

export const Footer = () => {
  const [email, setEmail] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Por favor, insira um e-mail válido.');
      return;
    }

    setIsSubmitting(true);

    try {
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_CONTACT,
        {
          nome: 'Newsletter',
          email,
          assunto: 'Nova inscrição na newsletter',
          mensagem: `${email} se inscreveu na newsletter da Aura Rosa.`
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );
      toast.success('Inscrito com sucesso!');
      setEmail('');
    } catch (error) {
      toast.error('Erro ao inscrever. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-white border-t border-stone-100 mt-20" data-testid="main-footer">
      <div className="max-w-[1400px] mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          <div>
            <h3 className="font-heading text-xl font-bold mb-4 text-accent">AURA ROSA</h3>
            <p className="text-sm text-text-secondary mb-4">
              Beleza e elegância para realçar sua essência única.
            </p>
            <div className="flex space-x-4">
              <a href="mailto:contatoaurarosastore@gmail.com" className="hover:text-accent transition-colors" aria-label="Email" data-testid="email-link">
                <Mail className="w-5 h-5" />
              </a>
            </div>
          </div>

          <div>
            <h4 className="font-body font-semibold mb-4 uppercase tracking-widest text-sm">Shop</h4>

            <ul className="space-y-2 text-sm text-text-secondary">

            <li>
              <a href="/produtos?category=Maquiagem" className="hover:text-accent transition">
                Maquiagem
              </a>
            </li>

            <li>
              <a href="/produtos?category=Acessórios" className="hover:text-accent transition">
                Acessórios
              </a>
            </li>

            <li>
              <a href="/produtos?category=Skincare" className="hover:text-accent transition">
                Skincare
              </a>
            </li>

            <li>
              <a href="/produtos?category=Tecnologia de Beleza" className="hover:text-accent transition">
                Tecnologia de Beleza
              </a>
            </li>

</ul>
          </div>

          <div>
            <h4 className="font-body font-semibold mb-4 uppercase tracking-widest text-sm">Informações</h4>
            <ul className="space-y-2 text-sm text-text-secondary">
              <li><Link to="/sobre" className="hover:text-accent transition-colors">Sobre Nós</Link></li>
              <li><Link to="/contato" className="hover:text-accent transition-colors">Contato</Link></li>
              <li><Link to="/faq" className="hover:text-accent transition-colors">FAQ</Link></li>
              <li><Link to="/termos-de-uso" className="hover:text-accent transition-colors">Termos de Uso</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-body font-semibold mb-4 uppercase tracking-widest text-sm">Newsletter</h4>
            <p className="text-sm text-text-secondary mb-4">
              Receba novidades e ofertas exclusivas.
            </p>
            <form onSubmit={handleNewsletterSubmit} className="flex gap-2" data-testid="newsletter-form">
              <Input
                type="email"
                placeholder="Seu e-mail"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="flex-1"
                data-testid="newsletter-email-input"
              />
              <Button
                type="submit"
                disabled={isSubmitting}
                className="bg-accent text-white hover:bg-accent/90 rounded-full"
                data-testid="newsletter-submit-button"
              >
                {isSubmitting ? '...' : 'Enviar'}
              </Button>
            </form>
          </div>

          <div>
            <h4 className="font-body font-semibold mb-4 uppercase tracking-widest text-sm">Meios de Pagamento</h4>
            <p className="text-sm text-text-secondary mb-4">
              Pagamento seguro no checkout.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              {paymentMethods.map(({ label, icon: Icon, color }) => (
                <span
                  key={label}
                  className="inline-flex h-7 items-center justify-center"
                  title={label}
                  aria-label={label}
                >
                  <Icon className="h-4 w-6" style={{ color }} aria-hidden="true" />
                </span>
              ))}
              <span className="inline-flex h-7 items-center rounded border border-stone-200 px-2 text-[11px] text-text-muted">
                Boleto
              </span>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-stone-100 text-center text-sm text-text-muted">
          <div className="flex flex-col items-center justify-center gap-3">
            <p>&copy; {new Date().getFullYear()} Aura Rosa. Todos os direitos reservados.</p>
          </div>
        </div>
      </div>
    </footer>
  );
};
